import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  EnvelopeIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Icon } from "@iconify/react";
import {
  contactSignature,
  formatOverviewReadOnlyContact,
  normalizeOverviewContact,
  validateEmailDomainAgainstOrgUsers,
} from "../utils/vqqOverviewContacts";
import {
  resolveApiErrorMessage,
  resolveDisplayedPlatformRole,
} from "../utils/overviewContactsShared";
import OverviewContactNotesRow from "./OverviewContactNotesRow";

const EMPTY_NEW_CONTACT = {
  firstName: "",
  lastName: "",
  title: "",
  email: "",
  _touched: {},
  domainError: null,
};

/**
 * VQQ Overview multi-contact manager (vendor or sponsor).
 * Visual/behavior baseline: Innovation-Lab VendorContactsField + Request VQQ chips.
 */
export default function VqqOverviewContactsField({
  contacts = [],
  availableContacts = [],
  onSave,
  onResendInvite,
  onCreateUser,
  canEdit = false,
  mode = "vendor",
  entityLabel = "VQQ",
  showPlatformRoles = true,
  assignedPlatformRole = "VQQ Manager",
  useAddContactLabels = false,
  saving = false,
  knownOrgEmails = null,
  showContactNotes = false,
  notesEditable = false,
  contactNotesByEmail = {},
  onContactNotesChange,
}) {
  const isSponsorMode = mode === "sponsor";
  const orgKind = isSponsorMode ? "sponsor" : "vendor";

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAddingNewContact, setIsAddingNewContact] = useState(false);
  const [newContactForm, setNewContactForm] = useState(EMPTY_NEW_CONTACT);
  const [contactPendingRemove, setContactPendingRemove] = useState(null);
  const [inviteToast, setInviteToast] = useState(null);
  const [resending, setResending] = useState(false);
  const [draftContacts, setDraftContacts] = useState(() => contacts || []);
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);
  const resendingRef = useRef(false);

  const addContactLabels = isSponsorMode ? false : useAddContactLabels;
  const createFooterLabel = addContactLabels
    ? `Add a contact not listed above to this ${entityLabel}`
    : `Create a user not listed above and add them to this ${entityLabel}`;
  const createButtonLabel = addContactLabels ? "Add contact" : "Create User";
  const creatingPlaceholder = addContactLabels
    ? "Adding new contact..."
    : "Creating new user...";

  const enrichContacts = (list) =>
    (list || [])
      .map((c, i) => normalizeOverviewContact(c, i))
      .filter(Boolean)
      .map((contact) => {
        const fromOrg = availableContacts.find(
          (c) => c.email?.toLowerCase() === contact.email?.toLowerCase(),
        );
        return {
          ...contact,
          role: fromOrg?.role || contact.role || "Team Member",
          userRole: resolveDisplayedPlatformRole(
            contact,
            fromOrg,
            assignedPlatformRole,
          ),
          isPrimary: isSponsorMode
            ? Boolean(fromOrg?.isPrimary)
            : Boolean(fromOrg?.isPrimary || contact.isPrimary),
          isRequestContact: Boolean(fromOrg?.isRequestContact || contact.isRequestContact),
        };
      });

  const savedContacts = useMemo(
    () => enrichContacts(contacts),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- enrich against latest org roster
    [contacts, availableContacts, assignedPlatformRole],
  );

  const savedSignature = useMemo(
    () => contactSignature(savedContacts),
    [savedContacts],
  );

  useEffect(() => {
    setDraftContacts(savedContacts);
  }, [savedSignature]); // eslint-disable-line react-hooks/exhaustive-deps -- reset draft only when saved set changes

  const draft = useMemo(
    () => enrichContacts(draftContacts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftContacts, availableContacts, assignedPlatformRole],
  );

  const isDirty = contactSignature(draft) !== savedSignature;

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const inviteToastId = inviteToast?.id;
  useEffect(() => {
    if (!inviteToastId) return undefined;
    const timer = setTimeout(() => setInviteToast(null), 3200);
    return () => clearTimeout(timer);
  }, [inviteToastId]);

  const showInviteToast = (message, tone = "success") => {
    const id = Date.now();
    setInviteToast({ message, id, tone });
  };

  const updateDraft = (nextContacts) => {
    setDraftContacts(
      nextContacts.map((c, i) => normalizeOverviewContact(c, i)).filter(Boolean),
    );
  };

  const requestRemove = (contact) => {
    const normalized = normalizeOverviewContact(contact);
    if (!normalized) return;
    if (draft.length <= 1) return;
    setContactPendingRemove(normalized);
    setIsDropdownOpen(false);
  };

  const addContactToDraft = (contact) => {
    const normalized = normalizeOverviewContact(contact);
    if (!normalized) return;
    if (draft.some((c) => c.email === normalized.email)) return;
    updateDraft([...draft, normalized]);
    setIsAddingNewContact(false);
    setNewContactForm(EMPTY_NEW_CONTACT);
  };

  const handleToggleContact = (contact) => {
    const normalized = normalizeOverviewContact(contact);
    if (!normalized) return;
    const isSelected = draft.some((c) => c.email === normalized.email);
    if (isSelected) {
      requestRemove(normalized);
      return;
    }
    addContactToDraft(contact);
  };

  const handleConfirmRemove = () => {
    if (!contactPendingRemove) return;
    if (draft.length <= 1) {
      setContactPendingRemove(null);
      return;
    }
    updateDraft(draft.filter((c) => c.email !== contactPendingRemove.email));
    setContactPendingRemove(null);
  };

  const handleDiscard = () => {
    setDraftContacts(savedContacts);
    setIsAddingNewContact(false);
    setNewContactForm(EMPTY_NEW_CONTACT);
    setIsDropdownOpen(false);
    setContactPendingRemove(null);
  };

  const handleSave = async () => {
    if (!isDirty || draft.length === 0 || saving) return;
    try {
      await onSave?.(draft);
    } catch (err) {
      showInviteToast(
        resolveApiErrorMessage(err, "Could not save contacts. Please try again."),
        "error",
      );
    }
  };

  const updateNewContactField = (field, value) => {
    setNewContactForm((prev) => ({
      ...prev,
      [field]: value,
      domainError: field === "email" ? null : prev.domainError,
    }));
  };

  const startAddNewContact = () => {
    setIsAddingNewContact(true);
    setIsDropdownOpen(false);
    setNewContactForm(EMPTY_NEW_CONTACT);
  };

  const cancelAddNewContact = () => {
    setIsAddingNewContact(false);
    setNewContactForm(EMPTY_NEW_CONTACT);
  };

  const submitNewContact = async () => {
    const firstName = newContactForm.firstName.trim();
    const lastName = newContactForm.lastName.trim();
    const title = newContactForm.title.trim();
    const email = newContactForm.email.trim().toLowerCase();

    if (!firstName || !lastName || !title || !email) {
      setNewContactForm((prev) => ({
        ...prev,
        _touched: { firstName: true, lastName: true, title: true, email: true },
      }));
      return;
    }

    const domainCheck = validateEmailDomainAgainstOrgUsers(
      email,
      knownOrgEmails ?? availableContacts.map((c) => c.email),
    );
    if (!domainCheck.ok) {
      setNewContactForm((prev) => ({
        ...prev,
        _touched: { ...prev._touched, email: true },
        domainError: domainCheck.message,
      }));
      return;
    }

    const draftContact = normalizeOverviewContact({
      first_name: firstName,
      last_name: lastName,
      job_title: title,
      email,
      userRole: assignedPlatformRole,
      isAdHoc: true,
    });
    if (!draftContact) return;

    setCreating(true);
    try {
      if (onCreateUser) {
        await onCreateUser({
          first_name: firstName,
          last_name: lastName,
          job_title: title,
          email,
        });
      }
      if (!draft.some((c) => c.email === draftContact.email)) {
        updateDraft([
          ...draft,
          { ...draftContact, userRole: assignedPlatformRole, isAdHoc: true },
        ]);
      }
      cancelAddNewContact();
    } catch (err) {
      setNewContactForm((prev) => ({
        ...prev,
        domainError: resolveApiErrorMessage(
          err,
          "Could not create user. Please try again.",
        ),
      }));
    } finally {
      setCreating(false);
    }
  };

  const deliverResend = async (targets, options, successMessage) => {
    const list = Array.isArray(targets) ? targets : [targets];
    if (!list.length || resendingRef.current) return;
    resendingRef.current = true;
    setResending(true);
    try {
      const result = options
        ? await onResendInvite?.(list, options)
        : await onResendInvite?.(list);
      showInviteToast(
        result?.skipped_emails?.length ? result.message : successMessage(list),
      );
    } catch (err) {
      showInviteToast(
        resolveApiErrorMessage(
          err,
          "Could not resend the invite. Please try again.",
        ),
        "error",
      );
    } finally {
      resendingRef.current = false;
      setResending(false);
    }
  };

  const handleResend = (targets) =>
    deliverResend(targets, undefined, (list) => {
      const names = list.map((c) => c.name).join(", ");
      return list.length === 1
        ? `Invite resent to ${names}`
        : `Invite resent to ${list.length} contacts`;
    });

  const handleResendAll = () =>
    deliverResend(
      savedContacts,
      { resendAll: true },
      (list) => `Invite resent to ${list.length} contacts`,
    );

  const canRemove = draft.length > 1;
  const fieldRingInvalid = (value, touched) =>
    !value.trim() && touched ? "ring-red-300" : "ring-gray-200";

  const renderContactMetaLine = (contact) => (
    <>
      {contact.role || "Team Member"}
      <span className="text-gray-300 mx-1.5">·</span>
      <span className="text-gray-600">{contact.email}</span>
      {showPlatformRoles && (
        <>
          <span className="text-gray-300 mx-1.5">·</span>
          <span className="text-gray-700 font-medium">
            {contact.userRole || "No platform role"}
          </span>
        </>
      )}
    </>
  );

  // Pre-accept only: after In Progress (HC 409 on invite mutations) match the
  // legacy Overview read-only rows (icon + "Name | email"), no managers/dropdowns.
  if (!canEdit) {
    return (
      <p className="space-y-1">
        {savedContacts.length === 0 ? (
          <span className="flex items-center gap-x-2">
            <Icon
              icon="fa-solid:address-book"
              className="w-4 h-4 text-black-600"
            />
            -
          </span>
        ) : (
          savedContacts.map((contact) => (
            <span
              key={contact.email}
              className="flex items-center gap-x-2"
            >
              <Icon
                icon="fa-solid:address-book"
                className="w-4 h-4 text-black-600"
              />
              {formatOverviewReadOnlyContact(contact)}
            </span>
          ))
        )}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="sm:max-w-xl relative" ref={dropdownRef}>
        <div
          role="button"
          tabIndex={0}
          className={`flex flex-wrap items-center gap-1.5 rounded-md shadow-sm ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 hover:bg-gray-50 transition-colors min-h-[38px] px-2 py-1.5 cursor-pointer ${
            isDirty ? "ring-amber-300 bg-amber-50/30" : "ring-gray-200"
          }`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsDropdownOpen((v) => !v);
            }
          }}
        >
          {draft.length > 0 ? (
            draft.map((contact) => (
              <span
                key={contact.email}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
              >
                {contact.name}{" "}
                <span className="text-indigo-400 font-normal">
                  / {contact.role || "Team Member"}
                </span>
                {showPlatformRoles && contact.userRole && (
                  <span className="text-indigo-500 font-normal">
                    · {contact.userRole}
                  </span>
                )}
                {contact.isPrimary && (
                  <span className="text-amber-600 text-[9px] font-semibold ml-0.5">
                    ★
                  </span>
                )}
                {canRemove && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      requestRemove(contact);
                    }}
                    className="ml-0.5 inline-flex items-center text-indigo-400 hover:text-indigo-600"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          ) : isAddingNewContact ? (
            <span className="text-sm text-gray-500">{creatingPlaceholder}</span>
          ) : (
            <span className="text-sm text-gray-500">
              Select {orgKind} contact(s)
            </span>
          )}
          <span className="pointer-events-none ml-auto flex items-center">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </div>

        {isDropdownOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {availableContacts.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-500">
                No eligible {orgKind} admin or manager contacts for this
                organization.
              </div>
            )}
            {availableContacts.map((contact) => {
              const isSelected = draft.some((c) => c.email === contact.email);
              const blockDeselect = isSelected && draft.length <= 1;
              return (
                <div
                  key={contact.email}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                    isSelected
                      ? "text-indigo-600 bg-indigo-50"
                      : "text-gray-900 hover:bg-gray-50"
                  } ${blockDeselect ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    if (blockDeselect) return;
                    handleToggleContact(contact);
                  }}
                  onKeyDown={(e) => {
                    if (blockDeselect) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleToggleContact(contact);
                    }
                  }}
                  title={
                    blockDeselect
                      ? "Add another contact before removing this one"
                      : undefined
                  }
                >
                  <span className="block truncate">
                    {contact.name}{" "}
                    <span className={isSelected ? "text-indigo-400" : "text-gray-500"}>
                      / {contact.role || "Team Member"}
                    </span>
                    {contact.isPrimary && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                        Primary
                      </span>
                    )}
                    {!contact.isPrimary && contact.isRequestContact && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                        Request
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-gray-500">
                    {contact.email}
                    {showPlatformRoles && contact.userRole && (
                      <span className="text-gray-400"> · {contact.userRole}</span>
                    )}
                  </span>
                  {isSelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <CheckIcon
                        className="h-5 w-5 text-indigo-600"
                        aria-hidden="true"
                      />
                    </span>
                  )}
                </div>
              );
            })}
            <div
              role="button"
              tabIndex={0}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 text-indigo-600 hover:bg-indigo-50${
                availableContacts.length > 0 ? " border-t border-gray-100" : ""
              }`}
              onClick={startAddNewContact}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  startAddNewContact();
                }
              }}
            >
              <span className="block truncate italic text-gray-500">
                {createFooterLabel}
              </span>
            </div>
          </div>
        )}
      </div>

      {contactPendingRemove && (
        <div
          role="alertdialog"
          aria-labelledby={`remove-${orgKind}-contact-title`}
          aria-describedby={`remove-${orgKind}-contact-desc`}
          className="sm:max-w-xl rounded-md border border-amber-200 bg-amber-50 px-3 py-3"
        >
          <div className="flex gap-2">
            <ExclamationTriangleIcon
              className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="min-w-0 space-y-2">
              <p
                id={`remove-${orgKind}-contact-title`}
                className="text-sm font-semibold text-gray-900"
              >
                Remove {contactPendingRemove.name}?
              </p>
              <p
                id={`remove-${orgKind}-contact-desc`}
                className="text-xs text-gray-600"
              >
                {isSponsorMode
                  ? `By removing ${contactPendingRemove.name}, they will no longer be assigned as a sponsor contact on this ${entityLabel}.`
                  : `By removing ${contactPendingRemove.name}, they will no longer be the primary user responsible for completing the ${entityLabel}.`}
              </p>
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleConfirmRemove}
                  className="rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-500"
                >
                  Remove contact
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setContactPendingRemove(null)}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                >
                  Keep contact
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddingNewContact && (
        <div className="space-y-3 sm:max-w-xl">
          <div className="flex gap-4">
            <div className="flex-1">
              <div
                className={`flex rounded-md shadow-sm ring-1 ring-inset ${fieldRingInvalid(
                  newContactForm.firstName,
                  newContactForm._touched?.firstName,
                )} focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600`}
              >
                <input
                  type="text"
                  required
                  value={newContactForm.firstName}
                  onChange={(e) =>
                    updateNewContactField("firstName", e.target.value)
                  }
                  onBlur={() =>
                    setNewContactForm((prev) => ({
                      ...prev,
                      _touched: { ...prev._touched, firstName: true },
                    }))
                  }
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                  placeholder="First name *"
                />
              </div>
            </div>
            <div className="flex-1">
              <div
                className={`flex rounded-md shadow-sm ring-1 ring-inset ${fieldRingInvalid(
                  newContactForm.lastName,
                  newContactForm._touched?.lastName,
                )} focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600`}
              >
                <input
                  type="text"
                  required
                  value={newContactForm.lastName}
                  onChange={(e) =>
                    updateNewContactField("lastName", e.target.value)
                  }
                  onBlur={() =>
                    setNewContactForm((prev) => ({
                      ...prev,
                      _touched: { ...prev._touched, lastName: true },
                    }))
                  }
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                  placeholder="Last name *"
                />
              </div>
            </div>
          </div>
          <div
            className={`flex rounded-md shadow-sm ring-1 ring-inset ${fieldRingInvalid(
              newContactForm.title,
              newContactForm._touched?.title,
            )} focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600`}
          >
            <input
              type="text"
              required
              value={newContactForm.title}
              onChange={(e) => updateNewContactField("title", e.target.value)}
              onBlur={() =>
                setNewContactForm((prev) => ({
                  ...prev,
                  _touched: { ...prev._touched, title: true },
                }))
              }
              className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
              placeholder="Job title *"
            />
          </div>
          <div
            className={`flex rounded-md shadow-sm ring-1 ring-inset ${
              newContactForm.domainError
                ? "ring-red-300"
                : fieldRingInvalid(
                    newContactForm.email,
                    newContactForm._touched?.email,
                  )
            } focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600`}
          >
            <input
              type="email"
              required
              value={newContactForm.email}
              onChange={(e) => updateNewContactField("email", e.target.value)}
              onBlur={() =>
                setNewContactForm((prev) => ({
                  ...prev,
                  _touched: { ...prev._touched, email: true },
                }))
              }
              className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
              placeholder="Email *"
            />
          </div>
          {newContactForm.domainError && (
            <p className="text-xs text-red-600">{newContactForm.domainError}</p>
          )}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={submitNewContact}
              disabled={creating}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
            >
              {createButtonLabel}
            </button>
            <button
              type="button"
              onClick={cancelAddNewContact}
              className="text-sm font-semibold text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {draft.length > 0 && (
        <ul className="space-y-2 sm:max-w-xl">
          {draft.map((contact) => (
            <li
              key={`detail-${contact.email}`}
              className="flex items-start justify-between gap-3"
            >
              <div className="min-w-0 text-xs text-gray-500">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-gray-700">{contact.name}</span>
                  {contact.isPrimary && (
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                      Primary
                    </span>
                  )}
                </div>
                <div className="truncate">{renderContactMetaLine(contact)}</div>
                {showContactNotes ? (
                  <OverviewContactNotesRow
                    contact={contact}
                    contactNotesByEmail={contactNotesByEmail}
                    notesEditable={notesEditable}
                    onNotesChange={onContactNotesChange}
                    idPrefix={`${orgKind}-contact`}
                  />
                ) : null}
              </div>
              {!isDirty && (
                <button
                  type="button"
                  onClick={() => handleResend([contact])}
                  disabled={resending}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={`Resend invite to ${contact.name}`}
                >
                  <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Resend Invite
                </button>
              )}
            </li>
          ))}
          {!isDirty && draft.length > 1 && (
            <li>
              <button
                type="button"
                onClick={handleResendAll}
                disabled={resending}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Resend Invite to all
              </button>
            </li>
          )}
        </ul>
      )}

      {isDirty && (
        <div className="flex flex-wrap items-center gap-3 sm:max-w-xl">
          <button
            type="button"
            onClick={handleSave}
            disabled={draft.length === 0 || saving}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save contacts
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            className="text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            Discard
          </button>
          <p className="w-full text-xs text-amber-700">
            Save to apply this {orgKind} contact selection.
          </p>
        </div>
      )}

      {!canRemove && draft.length === 1 && (
        <p className="text-xs text-gray-500">
          Add another contact before removing the current one.
        </p>
      )}

      {inviteToast && (
        <div
          className={
            inviteToast.tone === "error"
              ? "rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              : "rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700"
          }
        >
          {inviteToast.message}
        </div>
      )}
    </div>
  );
}
